/**
 * Auth — Firebase npm (website-9efa7). Solo Google + allowlist ANALYTICS_ADMINS.
 */
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { FIREBASE_CONFIG } from '../config/firebase-config.js';

let persistenceStarted = false;
const googleAuthProvider = new GoogleAuthProvider();
googleAuthProvider.setCustomParameters({ prompt: 'select_account' });

function getFirebaseApp() {
  if (getApps().length) return getApp();
  return initializeApp(FIREBASE_CONFIG);
}

export function getAnalyticsAuth() {
  return getAuth(getFirebaseApp());
}

/** iPhone / Android phone / iPad (incl. iPadOS que se reporta como Mac). */
export function prefersRedirectAuth() {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  if (/iPhone|iPod|Android.+Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }
  if (/iPad/i.test(ua)) return true;
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true;
  if (navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1) {
    return true;
  }
  return false;
}

/** WhatsApp, Gmail, Instagram, etc. — suelen bloquear JSONP a Apps Script. */
export function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  return /FBAN|FBAV|Instagram|Line\/|Twitter|WhatsApp|Telegram|GSA\/|; wv\)/i.test(ua);
}

function kickPersistence() {
  if (persistenceStarted) return;
  persistenceStarted = true;
  try {
    setPersistence(getAnalyticsAuth(), browserLocalPersistence).catch(() => {});
  } catch (e) {
    /* ignore */
  }
}

function withTimeout(promise, ms) {
  return Promise.race([
    Promise.resolve(promise).catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), ms))
  ]);
}

export async function waitAuthReady(ms) {
  const timeoutMs = ms != null ? ms : 3500;
  kickPersistence();
  const auth = getAnalyticsAuth();
  try {
    await withTimeout(auth.authStateReady(), timeoutMs);
  } catch (e) {
    /* ignore */
  }
  return auth.currentUser || null;
}

export async function getIdToken(forceRefresh) {
  const user = await waitAuthReady(3500);
  if (!user) throw new Error('Sesión no iniciada.');
  return user.getIdToken(!!forceRefresh);
}

export async function peekAuthUser(timeoutMs) {
  return waitAuthReady(timeoutMs != null ? timeoutMs : 3500);
}

export async function requireFirebaseSession() {
  const user = await waitAuthReady(3500);
  if (!user) throw new Error('AUTH_REQUIRED');
  return { user, email: String(user.email || '').trim().toLowerCase() };
}

export function mapFirebaseAuthError(err) {
  const code = err && err.code ? String(err.code) : '';
  if (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/redirect-cancelled-by-user' ||
    code === 'auth/redirect-in-progress'
  ) {
    return '';
  }
  if (code === 'auth/popup-blocked') {
    return 'El navegador bloqueó la ventana de Google. En el celular usá Safari o Chrome.';
  }
  if (code === 'auth/user-disabled') return 'Usuario deshabilitado.';
  if (code === 'auth/operation-not-allowed') {
    return 'El acceso con Google no está habilitado. Contacta al administrador.';
  }
  if (code === 'auth/unauthorized-domain') {
    return 'Este dominio no está autorizado para login con Google.';
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'Ese correo existe con otro método. Contacta al administrador.';
  }
  if (code === 'auth/too-many-requests') return 'Demasiados intentos. Espera un momento.';
  if (code === 'auth/network-request-failed') return 'Error de red con Firebase.';
  return (err && err.message) || 'No se pudo iniciar sesión con Google.';
}

export function mapAnalyticsSecurityError(err) {
  const code = err && err.code ? String(err.code) : '';
  const msg = String((err && err.message) || '');
  if (code === 'ORIGIN_DENIED' || /ORIGIN_DENIED|Origen no permitido|Falta parámetro origin/i.test(msg)) {
    return 'Origen no autorizado. Abrí desde https://analytics.gicapital.cl/ (o el dominio autorizado).';
  }
  if (
    code === 'CAPTCHA_REQUIRED' ||
    code === 'CAPTCHA_INVALID' ||
    code === 'CAPTCHA_HOSTNAME' ||
    code === 'CAPTCHA_ERROR' ||
    /captcha|Turnstile|CAPTCHA/i.test(msg)
  ) {
    return 'Se requiere captcha. Completalo e intenta de nuevo.';
  }
  if (code === 'AUTH_REQUIRED' || code === 'AUTH_INVALID' || /Token ausente|JWT|Firebase/i.test(msg)) {
    return 'Sesión inválida o expirada. Volvé a ingresar.';
  }
  if (
    code === 'MISCONFIGURED' ||
    /ANALYTICS_ADMINS|Usuarios Portal|TURNSTILE_SECRET|allowlist/i.test(msg)
  ) {
    return 'Configuración incompleta en analytics-api. Contacta al administrador.';
  }
  if (
    code === 'FORBIDDEN' ||
    /Sin permiso|módulo `analytics`|ANALYTICS_ADMINS/i.test(msg)
  ) {
    return 'No tienes permiso para acceder a Analytics. Contacta al administrador.';
  }
  if (/SpreadsheetApp|googleapis|planilla|permiso para llamar/i.test(msg)) {
    return 'No tienes permiso para acceder a Analytics. Contacta al administrador.';
  }
  if (/Tiempo de espera agotado|Error de red al llamar analytics-api/i.test(msg)) {
    return 'No se pudo conectar con Analytics. Reintentá en unos segundos.';
  }
  return msg || 'No se pudo validar el acceso a Analytics.';
}

/**
 * Completa signInWithRedirect al volver de Google. Llamar una vez al boot.
 * @returns {Promise<import('firebase/auth').User|null>}
 */
export async function consumeGoogleRedirectResult() {
  kickPersistence();
  const auth = getAnalyticsAuth();
  try {
    const cred = await Promise.race([
      getRedirectResult(auth),
      new Promise(function (resolve) {
        setTimeout(function () {
          resolve(null);
        }, 15000);
      })
    ]);
    return cred && cred.user ? cred.user : null;
  } catch (err) {
    const code = err && err.code ? String(err.code) : '';
    if (code === 'auth/redirect-cancelled-by-user') return null;
    throw err;
  }
}

export async function loginWithGoogle() {
  kickPersistence();
  await withTimeout(Promise.resolve(), 0);
  const auth = getAnalyticsAuth();
  if (prefersRedirectAuth()) {
    await signInWithRedirect(auth, googleAuthProvider);
    const pending = new Error('REDIRECT_IN_PROGRESS');
    pending.code = 'auth/redirect-in-progress';
    throw pending;
  }
  const cred = await signInWithPopup(auth, googleAuthProvider);
  return cred.user;
}

export async function logoutAnalytics() {
  await signOut(getAnalyticsAuth());
}
